/**
 * What both clients' 15-min JWT caches (`flashka-web/src/auth/better/client.ts`,
 * `flashka-app/src/auth/better/jwt.ts`) must agree on: how long a token lives,
 * and when the sync socket says the cached one is dead.
 */

/**
 * How long the token lives, in seconds, as the SERVER that minted it sees it:
 * `exp − iat`, both on the server's clock. A cache counts that lifetime from
 * the moment it asked for the token, on ITS OWN clock — so a device clock that
 * is minutes off never decides when a token expires. Comparing the server's
 * `exp` against `Date.now()` did (FLASHKA-APP-2R residual 401s with a Bearer;
 * the party worker's JWTExpired, FLASHKA-DURABLE-OBJECT-J): a device running
 * 5 min slow kept sending a token for 5 min after the server called it dead.
 *
 * No `iat`: the local clock is all there is, and never more than `fallbackS`.
 * Unreadable: `fallbackS`.
 */
export function jwtLifetimeSeconds(
  token: string,
  fallbackS: number,
  nowS: number = Date.now() / 1000,
): number {
  try {
    // JWT payloads are base64url — translate before atob.
    const b64 = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const { exp, iat } = JSON.parse(globalThis.atob(b64)) as {
      exp?: unknown;
      iat?: unknown;
    };
    if (typeof exp === 'number' && typeof iat === 'number' && exp > iat) return exp - iat;
    if (typeof exp === 'number' && exp > 0) return Math.min(fallbackS, exp - nowS);
  } catch {
    /* fall through to the fallback */
  }
  return fallbackS;
}

type CredentialRejectionListener = (token: string | null) => void;
const credentialRejectionListeners = new Set<CredentialRejectionListener>();

/**
 * Subscribe to the sync socket being refused its token (a 4401 / 4403 from a
 * socket that CARRIED one). The JWT cache drops that token, so the reconnect's
 * params mint a fresh one instead of re-sending the dead one until it expires —
 * which is three refusals and a halted sync. Returns the unsubscribe.
 */
export function onSocketCredentialRejected(
  listener: CredentialRejectionListener,
): () => void {
  credentialRejectionListeners.add(listener);
  return () => credentialRejectionListeners.delete(listener);
}

/** Called by the sync engine; `token` is the one the refused socket carried. */
export function notifySocketCredentialRejected(token: string | null): void {
  for (const listener of credentialRejectionListeners) {
    try {
      listener(token);
    } catch {
      // A cache that fails to drop its token must not break the close handler.
    }
  }
}
